import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, Loader2 } from 'lucide-react';
import { documentAPI, PaymentReceiptData, ExpenseReceiptData } from '../../api/documents';
import { ReceiptPrintModal } from './ReceiptPrintModal';
import toast from 'react-hot-toast';

interface ReceiptButtonProps {
  type: 'payment' | 'expense';
  entityId: number;
  className?: string;
}

/**
 * Receipt button component for browser-based printing
 * Opens a modal preview and uses window.print() for native printing
 */
export const ReceiptButton: React.FC<ReceiptButtonProps> = ({
  type,
  entityId,
  className = '',
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<PaymentReceiptData | ExpenseReceiptData | null>(null);

  const handleClick = async () => {
    setLoading(true);
    try {
      // Fetch receipt data as JSON
      let data: PaymentReceiptData | ExpenseReceiptData;

      if (type === 'payment') {
        data = await documentAPI.getPaymentReceiptData(entityId);
      } else {
        data = await documentAPI.getExpenseReceiptData(entityId);
      }

      setReceiptData(data);
      setModalOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('documents.generateError'));
    } finally {
      setLoading(false);
    }
  };

  const label = type === 'payment'
    ? t('documents.generatePaymentReceipt')
    : t('documents.generateExpenseReceipt');

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            {t('common.loading')}
          </>
        ) : (
          <>
            <Printer className="w-4 h-4 mr-1.5" />
            {label}
          </>
        )}
      </button>

      {/* Modal for browser-based printing */}
      {receiptData && (
        <ReceiptPrintModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          type={type}
          data={receiptData}
        />
      )}
    </>
  );
};
